import { useSelector } from 'react-redux';
import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import ApiPatientConversationsManager from 'services/api/ApiPatientConversationsManager';
import { selectCurrentUser } from 'store/users/selectors';
import syncBadgeCount from 'utils/syncBadgeCount';
import { resetUnreadMessages } from './conversationListMutators';
import { useUpdateConversations } from './useConversations';

/**
 * Stable hook to mark a conversation as read on the server and locally.
 *
 * Extracted from useLoadConversations so any component can mark a conversation as read
 * without also (re)triggering the heavy conversations list fetch. The list fetch is
 * mounted once at the Shell level (gap-recovery on websocket reconnect remains there).
 */
export default function useMarkConversationAsRead() {
  const currentUser = useSelector(selectCurrentUser);
  const updateConversations = useUpdateConversations();

  return async function markConversationAsRead(conversationIdToMark: string) {
    try {
      if (currentUser?.is_caregiver) {
        await ApiDoctorConversationsManager.markConversationAsRead(conversationIdToMark);
      } else {
        await ApiPatientConversationsManager.markConversationAsRead(conversationIdToMark);
      }
      // The server has now committed the read status, so the OS badge can be
      // resynced immediately instead of waiting on the async push the backend
      // also sends (which can race with other badge updates or arrive late).
      syncBadgeCount();
    } catch {
      // Still clear locally so the UI is not stuck if the request fails.
    }
    updateConversations(resetUnreadMessages(conversationIdToMark));
  };
}
